/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */
import { createSelector } from 'reselect';
import { isEmpty, groupBy, sortBy, uniqBy } from 'lodash';
import {
  isViewOnlyAccount,
  isBrokerage,
  isWorkplaceRetirement
} from 'services/Products';
import { isHiddenAccount, isPurgedAccount } from 'services/Accounts';
import { isSalesforceRepEnabled } from 'state/view/ViewSelectors';
import {
  getAccountsById,
  getAccounts,
  getHasAnyStopTransfer
} from 'state/account/AccountSelectors';

import {
  isQualifiedAccount,
  isReadyForValidation,
  isInitiationFail,
  isFailed, isDeleted
} from "services/LinkedBankAccounts";
import { dayDifference } from 'services/Dates';

const getBankProfile = (state) => state.bankProfile;

export const getBankAccountToUpdate = (state) => state.bankAccountToUpdate;

export const getAddUpdatedAssociations = (state) =>
  state.updatedAddAssociations;

export const getDeleteUpdatedAssociations = (state) =>
  state.updatedDeleteAssociations;

export const getBanksInErrorDepositVerification = (state) =>
  state.banksInErrorDepositVerification;

export const getBankModalState = (state) => state.bankModalState;

export const getEligibleAccounts = (state) => state.eligibleAgreementsForBank;

export const getBankAccounts = createSelector(
  getBankProfile,
  isSalesforceRepEnabled,
  (bankProfile, isSalesforceRepEnabled) => {
    const banks = bankProfile.bankAccounts || [];
    if (!isSalesforceRepEnabled) {
      return banks.filter(
        bank => (
          !isDeleted(bank.bankVerificationStatus) &&
          !isInitiationFail(bank.bankVerificationStatus) &&
          !(
            isFailed(bank.bankVerificationStatus) &&
            (Math.abs(dayDifference(bank.bankAccountLastUpdatedTimeStamp)) > 30)
          )
        )
      );
    }
    return banks;
  }
);

export const getEligibleLBAAccount = createSelector(getAccounts, (accounts) => {
  return accounts.filter(
    (account) =>
      !isPurgedAccount(account) &&
      !isHiddenAccount(account) &&
      !isViewOnlyAccount(account) &&
      !isBrokerage(account.productType) &&
      !isWorkplaceRetirement(account.productType) &&
      isQualifiedAccount(account.accountTypeId) &&
      account.actionEnabled
  );
});

export const canViewBankingPage = createSelector(
  getEligibleLBAAccount,
  (accounts) => {
    return accounts.length !== 0;
  }
);

export const getLinkedAccountsOrderedByTypeName = createSelector(
  getAccountsById,
  getBankAccounts,
  (accountsById, linkedBankAccounts) => {
    const bankAccounts = linkedBankAccounts.map((bankAccount) => {
      const { bankAssociations: associatedAccounts } = bankAccount;

      const uniqAccounts = uniqBy(
        associatedAccounts,
        (a) => a.accountDescriptor && a.accountDescriptor.accountId
      );
      const formattedAccounts = uniqAccounts.map(a => {
        const fullAccount = accountsById[a.accountDescriptor.accountId]

        if (fullAccount) {
          return {
            ...a,
            hiddenAccount: fullAccount.hiddenAccount
          }
        }

        return {
          ...a
        }
      })

      return {
        ...bankAccount,
        bankAssociations: sortBy(formattedAccounts, ['accountType', 'accountName'])
      };
    });

    return sortBy(bankAccounts, [
      (x) => !isReadyForValidation(x.verification),
      'bankName',
      'accountOwners'
    ]);
  }
);


export const transformAccounts = (
  accounts,
  associations,
  updatedAssociations
) => {
  return accounts.map((account) => {
    const { accountId } = account;
    // const isPendingUpdate = find(associations, association => association.accountDescriptor.accountId === accountId && association.pendingUpdate);
    return {
      accountDescriptor: {
        ...account,
        checked: updatedAssociations.includes(accountId)
      }
    };
  });
};

export const getAddAccountsOrderedByTypeName = createSelector(
  getEligibleAccounts,
  getBankAccounts,
  getBankAccountToUpdate,
  getAddUpdatedAssociations,
  (accounts, linkedBankAccounts, bankAccountToUpdate, updatedAssociations) => {
    if (accounts && linkedBankAccounts && bankAccountToUpdate) {
      if (bankAccountToUpdate) {
        const { bankAssociations } = bankAccountToUpdate;
        const existingAccountIds = bankAssociations.map(
          (assoc) => assoc.accountDescriptor.accountId
        );
        const filteredAccounts = accounts.filter(
          (acct) => !existingAccountIds.includes(acct.accountId)
        );
        const transformedAccounts = transformAccounts(
          filteredAccounts,
          bankAssociations,
          updatedAssociations
        );
        return sortBy(transformedAccounts, ['accountType', 'accountName']);
      }
    }

    return [];
  }
);

export const getDeleteAccountsOrderedByTypeName = createSelector(
  getEligibleAccounts,
  getBankAccounts,
  getBankAccountToUpdate,
  getDeleteUpdatedAssociations,
  (accounts, linkedBankAccounts, bankAccountToUpdate, updatedAssociations) => {
    if (accounts && linkedBankAccounts && bankAccountToUpdate) {
      if (bankAccountToUpdate) {
        const { bankAssociations } = bankAccountToUpdate;
        const existingAccountIds = bankAssociations.map(
          (assoc) => assoc.accountDescriptor.accountId
        );
        const filteredAccounts = accounts.filter((acct) =>
          existingAccountIds.includes(acct.accountId)
        );
        const transformedAccounts = transformAccounts(
          filteredAccounts,
          bankAssociations,
          updatedAssociations
        );
        return sortBy(transformedAccounts, ['accountType', 'accountName']);
      }
    }

    return [];
  }
);

export const getHasLinkedBankAccounts = createSelector(
  getBankAccounts,
  (linkedBankAccounts) => !isEmpty(linkedBankAccounts)
);

export const showFundsBoxInfo = createSelector(
  getHasAnyStopTransfer,
  (hasAnyStopTransfer) => !hasAnyStopTransfer
);

export const showStopTransferBoxInfo = createSelector(
  getHasAnyStopTransfer,
  getHasLinkedBankAccounts,
  (hasAnyStopTransfer, hasLinkedBankAccounts) => {
    if (hasAnyStopTransfer && hasLinkedBankAccounts) {
      return true;
    }

    return false;
  }
);

export const showNewBankAccountButton = createSelector(
  getHasAnyStopTransfer,
  getHasLinkedBankAccounts,
  (hasAnyStopTransfer, hasLinkedBankAccounts) =>
    !hasAnyStopTransfer && hasLinkedBankAccounts
);

export const showLinkedBankAccountSummary = createSelector(
  getHasLinkedBankAccounts,
  (hasLinkedBankAccounts) => hasLinkedBankAccounts
);

export const showStopTransferBanner = createSelector(
  getHasAnyStopTransfer,
  getHasLinkedBankAccounts,
  (hasAnyStopTransfer, hasLinkedBankAccounts) =>
    hasAnyStopTransfer && hasLinkedBankAccounts
);

export const showStopTransferZeroState = createSelector(
  getHasAnyStopTransfer,
  getHasLinkedBankAccounts,
  (hasAnyStopTransfer, hasLinkedBankAccounts) => {
    if (hasAnyStopTransfer && !hasLinkedBankAccounts) {
      return true;
    }
    return false;
  }
);

export const showZeroState = createSelector(
  getHasAnyStopTransfer,
  getHasLinkedBankAccounts,
  (hasAnyStopTransfer, hasLinkedBankAccounts) =>
    !hasAnyStopTransfer && !hasLinkedBankAccounts
);

export function groupAccountsByType(accounts) {
  return groupBy(accounts, (account) => account && account.accountType);
}
